import { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UploadCloud, Loader2, FileCode2, Eye, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHdr = (token) => ({ Authorization: `Bearer ${token}` });

export default function OperationalFilesTab({ token }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewer, setViewer] = useState(null); // { filename, content }
  const replaceInputRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/dataset-ops/operational-files`, { headers: authHdr(token) });
      const json = await res.json();
      setFiles(json.files || []);
    } catch { toast.error("Failed to load operational files"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const uploadFile = async (f, targetFilename) => {
    const form = new FormData();
    form.append("file", f);
    if (targetFilename) form.append("target_filename", targetFilename);
    const res = await fetch(`${API}/api/admin/cr/dataset-ops/operational-files/upload`, {
      method: "POST", headers: authHdr(token), body: form,
    });
    const json = await res.json();
    if (!res.ok) { toast.error(json.detail || "Upload failed"); return false; }
    return true;
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Choose a .py/.json/.md/.txt file first"); return; }
    setUploading(true);
    try {
      if (await uploadFile(file)) {
        toast.success(`Saved ${file.name} to operational_files`);
        setFile(null);
        loadFiles();
      }
    } finally { setUploading(false); }
  };

  const handleReplaceChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    // Content is written under the ORIGINAL filename (replaceTarget), not f.name
    if (await uploadFile(f, replaceTarget)) {
      toast.success(`Replaced ${replaceTarget} with content from ${f.name}`);
      loadFiles();
    }
    setReplaceTarget(null);
  };

  const openViewer = async (filename) => {
    try {
      const res = await fetch(`${API}/api/admin/cr/dataset-ops/operational-files/${filename}/content`, { headers: authHdr(token) });
      const json = await res.json();
      if (!res.ok) { toast.error(json.detail || "Could not load file content"); return; }
      setViewer(json);
    } catch { toast.error("Network error"); }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API}/api/admin/cr/dataset-ops/operational-files/${deleteTarget}`, {
        method: "DELETE", headers: authHdr(token),
      });
      if (!res.ok) { toast.error("Delete failed"); return; }
      toast.success(`Removed ${deleteTarget} override — will fall back to disk`);
      loadFiles();
    } catch { toast.error("Network error"); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 border border-slate-200 shadow-none">
        <h3 className="font-semibold text-slate-800 mb-1">Upload Operational File</h3>
        <p className="text-xs text-slate-500 mb-4">
          Stored in MongoDB (<code>operational_files</code>) — survives redeploys. Uploading an existing filename replaces it.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            type="file" accept=".py,.json,.md,.txt"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="max-w-xs"
            data-testid="operational-file-input"
          />
          <Button onClick={handleUpload} disabled={uploading || !file} data-testid="operational-file-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-1.5" />}
            Upload
          </Button>
        </div>
      </Card>

      <Card className="p-5 border border-slate-200 shadow-none">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-800">Operational File Registry (MongoDB)</h3>
          <Button variant="outline" size="sm" onClick={loadFiles} disabled={loading} data-testid="refresh-operational-files-btn">
            <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
        ) : files.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No MongoDB overrides yet — all consumers are reading from disk.</p>
        ) : (
          <div className="overflow-x-auto" data-testid="operational-files-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-2 pr-4">Filename</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4 text-right">Size</th>
                  <th className="py-2 pr-4">Last Modified</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {files.map(f => (
                  <tr key={f.filename} className="border-b border-slate-100" data-testid={`operational-row-${f.filename}`}>
                    <td className="py-2 pr-4 flex items-center gap-1.5 text-slate-700"><FileCode2 className="w-3.5 h-3.5 text-slate-400" />{f.filename}</td>
                    <td className="py-2 pr-4"><Badge variant="secondary" className="text-xs">{f.file_type}</Badge></td>
                    <td className="py-2 pr-4 text-right text-slate-600 text-xs">{f.size_bytes} B</td>
                    <td className="py-2 pr-4 text-slate-500 text-xs">{new Date(f.updated_at).toLocaleString()}</td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openViewer(f.filename)} data-testid={`view-operational-file-${f.filename}`}>
                        <Eye className="w-3.5 h-3.5 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setReplaceTarget(f.filename); replaceInputRef.current?.click(); }} data-testid={`replace-operational-file-${f.filename}`}>
                        <UploadCloud className="w-3.5 h-3.5 text-sky-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(f.filename)} data-testid={`delete-operational-file-${f.filename}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <input ref={replaceInputRef} type="file" accept=".py,.json,.md,.txt" className="hidden" onChange={handleReplaceChange} data-testid="replace-operational-file-input" />
      </Card>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewer?.filename}</DialogTitle>
          </DialogHeader>
          <pre className="bg-slate-900 text-slate-100 text-xs rounded-lg p-3 max-h-[60vh] overflow-auto whitespace-pre-wrap" data-testid="operational-file-content-viewer">
            {viewer?.content}
          </pre>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove MongoDB override?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget}</strong> will be removed from <code>operational_files</code>. Consumers will fall back to the disk copy at /app/project/config/.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-operational-file">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-operational-file">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
